import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'app/rootReducer'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { setMobilePhone } from 'reducers/authentication/authenticationSlice'
import { setMobilePhone as setUserProfileMobilePhone } from 'reducers/userprofile/userprofileSlice'
import './styles.scss'
import {
  VERIFY_UPDATED_MOBILE_PHONE,
  VerifyUpdatedMobilePhoneResponse,
  VerifyUpdatedMobilePhoneResponseData,
  VerifyUpdatedMobilePhoneVariables,
  RESEND_UPDATED_MOBILE_PHONE_CODE,
  ResendUpdatedMobilePhoneCodeResponse,
  ResendUpdatedMobilePhoneCodeResponseData,
  CANCEL_UPDATED_MOBILE_PHONE,
  CancelUpdatedMobilePhoneResponse,
  CancelUpdatedMobilePhoneResponseData
} from 'share/mutations/account'
import GoBackHeader from 'components/organisms/GoBackHeader'
import UserProfileVerifyMobilePhoneForm from 'components/organisms/UserProfileVerifyMobilePhoneForm'
import { AuthVerifyContainer } from 'components/atoms/Layout'
import ErrorMessage from 'components/atoms/ErrorMessage'

interface Props {
  nextPage(page: string): void
  previousPage(page: string): void
}

const UserProfileVerifyMobilePhone = (props: Props): JSX.Element => {
  const { nextPage } = props
  const userprofile = useSelector((state: RootState) => state.userprofile)
  const dispatch = useDispatch()
  const [lastFourMobilePhone, setLastFourMobilePhone] = useState('')
  const [enteredCodeComplete, setEnteredCodeComplete] = useState(false)
  const [enteredCode, setEnteredCode] = useState('')
  const [errorMessage, setErrorMessage] =
    useState<string | null | undefined>(null)
  const authentication = useSelector((state: RootState) => state.authentication)
  const [verifyUpdatedMobilePhone, { loading }] = useMutation<
    VerifyUpdatedMobilePhoneResponse,
    VerifyUpdatedMobilePhoneVariables
  >(VERIFY_UPDATED_MOBILE_PHONE)
  const [resendUpdatedMobilePhoneCode, { loading: loadingPhoneCode }] =
    useMutation<ResendUpdatedMobilePhoneCodeResponse>(
      RESEND_UPDATED_MOBILE_PHONE_CODE
    )
  const [cancelUpdatedMobilePhone, { loading: loadingCancelMobilePhone }] =
    useMutation<CancelUpdatedMobilePhoneResponse>(CANCEL_UPDATED_MOBILE_PHONE)
  const loadingDelay = useLoadingDelay(
    loading || loadingPhoneCode || loadingCancelMobilePhone
  )

  function setLastFourHelper(): void {
    let lastFour = ''
    if (authentication) {
      if (authentication.account) {
        if (authentication.account.mobilePhone) {
          const phoneNumber = authentication.account.mobilePhone
          lastFour =
            phoneNumber && phoneNumber.length > 4
              ? phoneNumber.substring(phoneNumber.length - 4)
              : ''
        }
      }
    }
    setLastFourMobilePhone(lastFour)
  }

  useEffect(() => {
    setLastFourHelper()
  }, [authentication])

  async function verifyUpdatedMobilePhoneHelper(
    input: VerifyUpdatedMobilePhoneVariables
  ): Promise<VerifyUpdatedMobilePhoneResponseData> {
    const result: VerifyUpdatedMobilePhoneResponseData = {
      success: false,
      errorMessage: null
    }
    setErrorMessage(null)
    const errMsg = 'Verifying your Mobile Phone had an error. Please try again.'
    try {
      const verifyUpdatedMobilePhoneResult = await verifyUpdatedMobilePhone({
        variables: input
      })
      if (
        verifyUpdatedMobilePhoneResult.data?.accounts.verifyUpdatedMobilePhone
          .success
      ) {
        result.success = true
        nextPage('edit')
      } else {
        result.success = false
        result.errorMessage =
          verifyUpdatedMobilePhoneResult.data?.accounts.verifyUpdatedMobilePhone.errorMessage
        setErrorMessage(result.errorMessage || errMsg)
      }
    } catch (e) {
      result.success = false
      result.errorMessage = errMsg
      setErrorMessage(result.errorMessage)
    }
    return result
  }

  async function resendUpdatedMobilePhoneCodeHelper(): Promise<ResendUpdatedMobilePhoneCodeResponseData> {
    const result: ResendUpdatedMobilePhoneCodeResponseData = {
      success: false
    }

    setErrorMessage(null)
    const errMsg =
      'Error resending verification code for mobile phone. Please try again later.'
    try {
      setEnteredCode('')
      const resendUpdatedMobilePhoneCodeResult =
        await resendUpdatedMobilePhoneCode()
      if (
        resendUpdatedMobilePhoneCodeResult.data?.accounts
          .resendUpdatedMobilePhoneCode.success
      ) {
        result.success = true
      } else {
        result.success = false

        setErrorMessage(errMsg)
      }
    } catch (e) {
      result.success = false
      setErrorMessage(errMsg)
    }
    return result
  }

  async function cancelUpdatedMobilePhoneHelper(): Promise<CancelUpdatedMobilePhoneResponseData> {
    const result: CancelUpdatedMobilePhoneResponseData = {
      success: false,
      errorMessage: null,
      mobilePhoneNumber: null
    }

    setErrorMessage(null)
    const errMsg = 'Canceling your Mobile Phone had an error. Please try again.'
    try {
      const cancelUpdatedMobilePhoneResult = await cancelUpdatedMobilePhone()
      if (
        cancelUpdatedMobilePhoneResult.data?.accounts
          .cancelUpdateAccountMobilePhone.success
      ) {
        result.success = true
        result.mobilePhoneNumber =
          cancelUpdatedMobilePhoneResult.data?.accounts.cancelUpdateAccountMobilePhone.mobilePhoneNumber
        if (result.mobilePhoneNumber) {
          dispatch(setMobilePhone(result.mobilePhoneNumber))
          if (userprofile) {
            if (userprofile.mobilePhone) {
              dispatch(setUserProfileMobilePhone(result.mobilePhoneNumber))
            }
          }
        }

        nextPage('edit')
      } else {
        result.success = false
        result.errorMessage =
          cancelUpdatedMobilePhoneResult.data?.accounts.cancelUpdateAccountMobilePhone.errorMessage
        setErrorMessage(result.errorMessage || errMsg)
      }
    } catch (e) {
      result.success = false
      result.errorMessage = errMsg
      setErrorMessage(result.errorMessage || errMsg)
    }
    return result
  }

  const renderFailViolator = () => {
    return (
      <ErrorMessage>
        <span>{errorMessage}</span>
      </ErrorMessage>
    )
  }

  return (
    <React.Fragment>
      <GoBackHeader
        text='Change Mobile Phone'
        onClick={() => props.previousPage('edit')}
      />
      <AuthVerifyContainer data-testid='create-account-account-verify-phone-page'>
        {loadingDelay && loadingSpinnerPortal()}
        {errorMessage && renderFailViolator()}
        <h2 data-testid='create-account-account-verify-phone-header'>
          Verify your phone number.
        </h2>
        <span
          className='verify-message-text'
          data-testid='create-account-account-verify-phone-message'
        >
          A verification code has been sent to to your mobile number,
          <strong data-testid='create-account-account-verify-phone-last-four'>
            {' '}
            ***-***-{lastFourMobilePhone}.
          </strong>
        </span>
        <UserProfileVerifyMobilePhoneForm
          verifyUpdatedMobilePhoneHelper={verifyUpdatedMobilePhoneHelper}
          enteredCode={enteredCode}
          enteredCodeComplete={enteredCodeComplete}
          resendUpdatedMobilePhoneCodeHelper={
            resendUpdatedMobilePhoneCodeHelper
          }
          setEnteredCode={setEnteredCode}
          setEnteredCodeComplete={setEnteredCodeComplete}
          cancelUpdatedMobilePhoneHelper={cancelUpdatedMobilePhoneHelper}
        />
      </AuthVerifyContainer>
    </React.Fragment>
  )
}

export default UserProfileVerifyMobilePhone
